import React, { useState, useEffect } from 'react';
import { TrendingUp, Heart, Brain, Users, Award, Calendar, Loader, BarChart3 } from 'lucide-react';
import Layout from '../../Layout';
import ParentAPI from '../../services/ParentAPI';
import { showErrorNotification } from '../../utils/errorHandler';

const LearnerHolisticProgress = () => {
    const [selectedChild, setSelectedChild] = useState(null);
    const [children, setChildren] = useState([]);
    const [holisticData, setHolisticData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadChildren();
    }, []);

    useEffect(() => {
        if (selectedChild) {
            loadHolisticData();
        }
    }, [selectedChild]);

    const loadChildren = async () => {
        try {
            const childrenData = await ParentAPI.getChildren();
            setChildren(childrenData);
            
            if (childrenData.length > 0) {
                setSelectedChild(childrenData[0]);
            }
        } catch (err) {
            console.error('Error loading children:', err);
            showErrorNotification('Failed to load children data');
        } finally {
            setLoading(false);
        }
    };

    const loadHolisticData = async () => {
        if (!selectedChild) return;

        try {
            setLoading(true);
            const holisticProgress = await ParentAPI.getHolisticProgress(selectedChild.student_id);
            setHolisticData(holisticProgress);
        } catch (err) {
            console.error('Error loading holistic data:', err);
            showErrorNotification('Failed to load holistic progress data');
        } finally {
            setLoading(false);
        }
    };

    const handleChildSwitch = (childId) => {
        const child = children.find(c => c.student_id === childId);
        setSelectedChild(child);
    };

    if (loading && !selectedChild) {
        return (
            <Layout pageTitle="Learner's Holistic Progress">
                <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center">
                    <div className="text-center">
                        <Loader className="w-12 h-12 animate-spin mx-auto mb-4 text-teal-600" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Holistic Progress</h2>
                        <p className="text-gray-600">Please wait while we fetch progress information...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!selectedChild) {
        return (
            <Layout pageTitle="Learner's Holistic Progress">
                <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center">
                    <div className="text-center">
                        <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">No Child Selected</h2>
                        <p className="text-gray-600">Please select a child to view their holistic progress.</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle="Learner's Holistic Progress">
            <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-800">Holistic Progress</h1>
                                <p className="text-gray-600">Comprehensive view of {selectedChild?.name}'s overall development</p>
                            </div>
                            
                            {/* Child Selector */}
                            {children.length > 1 && (
                                <div className="relative">
                                    <select
                                        value={selectedChild?.student_id || ''}
                                        onChange={(e) => handleChildSwitch(e.target.value)}
                                        className="bg-teal-50 border border-teal-200 text-teal-800 px-4 py-2 rounded-lg font-medium appearance-none cursor-pointer pr-10"
                                    >
                                        {children.map(child => (
                                            <option key={child.student_id} value={child.student_id}>
                                                {child.name} - Grade {child.grade || 'N/A'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>

                    {holisticData && (
                        <>
                            {/* Main Progress Indicators */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                {/* Cognitive Development */}
                                <div className="bg-white rounded-2xl shadow-xl p-6">
                                    <div className="flex items-center mb-4">
                                        <div className="bg-blue-500 p-3 rounded-xl mr-4">
                                            <Brain className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-800">Cognitive</h3>
                                            <p className="text-sm text-gray-600">Thinking & Learning</p>
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-4xl font-bold text-blue-600 mb-2">{holisticData.cognitive}%</div>
                                        <div className="w-full bg-gray-200 rounded-full h-3">
                                            <div 
                                                className="bg-gradient-to-r from-blue-400 to-blue-600 h-3 rounded-full transition-all duration-500"
                                                style={{ width: `${holisticData.cognitive}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>

                                {/* Social-Emotional Development */}
                                <div className="bg-white rounded-2xl shadow-xl p-6">
                                    <div className="flex items-center mb-4">
                                        <div className="bg-green-500 p-3 rounded-xl mr-4">
                                            <Heart className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-800">Social-Emotional</h3>
                                            <p className="text-sm text-gray-600">Feelings & Relationships</p>
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-4xl font-bold text-green-600 mb-2">{holisticData.socialEmotional}%</div>
                                        <div className="w-full bg-gray-200 rounded-full h-3">
                                            <div 
                                                className="bg-gradient-to-r from-green-400 to-green-600 h-3 rounded-full transition-all duration-500"
                                                style={{ width: `${holisticData.socialEmotional}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>

                                {/* Physical Development */}
                                <div className="bg-white rounded-2xl shadow-xl p-6">
                                    <div className="flex items-center mb-4">
                                        <div className="bg-orange-500 p-3 rounded-xl mr-4">
                                            <Users className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-800">Physical</h3>
                                            <p className="text-sm text-gray-600">Motor Skills & Health</p>
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-4xl font-bold text-orange-600 mb-2">{holisticData.physical}%</div>
                                        <div className="w-full bg-gray-200 rounded-full h-3">
                                            <div 
                                                className="bg-gradient-to-r from-orange-400 to-orange-600 h-3 rounded-full transition-all duration-500"
                                                style={{ width: `${holisticData.physical}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Achievement Summary */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                <div className="bg-white rounded-2xl shadow-xl p-6 text-center">
                                    <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <TrendingUp className="w-8 h-8 text-purple-600" />
                                    </div>
                                    <div className="text-3xl font-bold text-purple-600 mb-2">{holisticData.monthlyProgress}</div>
                                    <div className="text-gray-600">Monthly Progress Points</div>
                                </div>

                                <div className="bg-white rounded-2xl shadow-xl p-6 text-center">
                                    <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Award className="w-8 h-8 text-blue-600" />
                                    </div>
                                    <div className="text-3xl font-bold text-blue-600 mb-2">{holisticData.goalsMet}</div>
                                    <div className="text-gray-600">Learning Goals Met</div>
                                </div>

                                <div className="bg-white rounded-2xl shadow-xl p-6 text-center">
                                    <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Award className="w-8 h-8 text-yellow-600" />
                                    </div>
                                    <div className="text-3xl font-bold text-yellow-600 mb-2">{holisticData.awards}</div>
                                    <div className="text-gray-600">Awards & Recognition</div>
                                </div>
                            </div>

                            {/* Progress Timeline */}
                            <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
                                <div className="flex items-center mb-6">
                                    <Calendar className="w-8 h-8 text-teal-600 mr-3" />
                                    <h3 className="text-2xl font-bold text-gray-800">Development Timeline</h3>
                                </div>

                                <div className="space-y-4">
                                    {[
                                        { month: 'January', cognitive: 85, social: 88, physical: 82, highlight: 'Improved problem-solving skills' },
                                        { month: 'February', cognitive: 87, social: 90, physical: 84, highlight: 'Better emotional regulation' },
                                        { month: 'March', cognitive: 89, social: 92, physical: 85, highlight: 'Enhanced motor coordination' }
                                    ].map((month, index) => (
                                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className="font-semibold text-gray-800">{month.month} 2024</h4>
                                                <span className="text-sm text-teal-600 font-medium">{month.highlight}</span>
                                            </div>
                                            <div className="grid grid-cols-3 gap-4">
                                                <div className="text-center">
                                                    <div className="text-sm text-gray-600">Cognitive</div>
                                                    <div className="text-lg font-bold text-blue-600">{month.cognitive}%</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="text-sm text-gray-600">Social-Emotional</div>
                                                    <div className="text-lg font-bold text-green-600">{month.social}%</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="text-sm text-gray-600">Physical</div>
                                                    <div className="text-lg font-bold text-orange-600">{month.physical}%</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Recommendations */}
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center mb-6">
                                    <BarChart3 className="w-8 h-8 text-teal-600 mr-3" />
                                    <h3 className="text-2xl font-bold text-gray-800">Development Recommendations</h3>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-blue-50 rounded-lg p-4">
                                        <h4 className="font-bold text-blue-800 mb-2">Cognitive Enhancement</h4>
                                        <ul className="space-y-1 text-sm text-blue-700">
                                            <li>• Engage in puzzle-solving activities</li>
                                            <li>• Practice pattern recognition games</li>
                                            <li>• Encourage questioning and exploration</li>
                                        </ul>
                                    </div>

                                    <div className="bg-green-50 rounded-lg p-4">
                                        <h4 className="font-bold text-green-800 mb-2">Social-Emotional Growth</h4>
                                        <ul className="space-y-1 text-sm text-green-700">
                                            <li>• Practice empathy through role-playing</li>
                                            <li>• Encourage group activities</li>
                                            <li>• Discuss feelings and emotions daily</li>
                                        </ul>
                                    </div>

                                    <div className="bg-orange-50 rounded-lg p-4">
                                        <h4 className="font-bold text-orange-800 mb-2">Physical Development</h4>
                                        <ul className="space-y-1 text-sm text-orange-700">
                                            <li>• Daily outdoor play activities</li>
                                            <li>• Fine motor skill exercises</li>
                                            <li>• Balance and coordination games</li>
                                        </ul>
                                    </div>

                                    <div className="bg-purple-50 rounded-lg p-4">
                                        <h4 className="font-bold text-purple-800 mb-2">Overall Wellness</h4>
                                        <ul className="space-y-1 text-sm text-purple-700">
                                            <li>• Maintain consistent sleep schedule</li>
                                            <li>• Balanced nutrition and hydration</li>
                                            <li>• Regular family bonding time</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default LearnerHolisticProgress;